import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
const lang = "ar";
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_ENDPOINTS_BASE_URL,
});

const fetcherFunction = async ({
  api,
  Session,
  lang,
  pageParam,
  limit,
  infiniteScroll,
  paginationData,
  addToken,
}) => {
  const headers = {
    "Content-Type": "application/json",
    lang,
  };
  const API = infiniteScroll
    ? `${api}page=${pageParam}&paginate=${limit}`
    : api;

  const res = await axiosInstance.get(API, {
    headers: headers,
    withCredentials: Session || addToken ? true : false,
  });

  return paginationData ? res.data?.data : res.data;
};

export const useFetchData = ({
  identifier,
  api,
  select,
  id = "",
  cacheTime,
  staleTime,
  enabled,
  addToken = true,
  QueryOptions,
}) => {
  const Session = true;

  const useQueryOptions = {
    select: (data) => {
      return select ? data?.data : data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
    cacheTime: cacheTime,
    staleTime: staleTime,
    retry: 1,
    ...QueryOptions,
  };
  return useQuery({
    queryKey: [identifier, id],
    queryFn: () => fetcherFunction({ api, Session, lang, addToken }),
    ...useQueryOptions,
  });
};
export const useFetchParallelData = (
  identifier,
  iterators,
  api,
  select,
  cacheTime = 5000,
  staleTime = 0,
  enabled = true,
  addToken = false
) => {
  const Session = true;

  const useQueryOptions = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
    cacheTime: cacheTime,
    staleTime: staleTime,
    retry: 1,
  };

  return useQueries({
    queries: iterators.map((iterator) => {
      return {
        queryKey: [identifier, iterator],
        queryFn: () =>
          fetcherFunction({
            api: api + iterator,
            Session,
            lang,
            addToken,
          }),
        ...useQueryOptions,
      };
    }),
  });
};

export const useFetchPaginatedData = (
  identifier,
  id = "",
  api,
  cacheTime = 500000,
  staleTime = 0,
  enabled = true,
  addToken = false,
  paginationData = true
) => {
  const Session = true;

  const useFetchPaginatedDataOptions = {
    select: (data) => {
      return data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
    cacheTime: cacheTime,
    refetchOnReconnect: true,
    staleTime: staleTime,
    retry: 1,
    keepPreviousData: true,
  };

  return useQuery({
    queryKey: [identifier, id],
    queryFn: () =>
      fetcherFunction({
        api,
        Session,
        lang,
        addToken,
        paginationData,
      }),
    ...useFetchPaginatedDataOptions,
  });
};
export const useFetchInfiniteScrollData = (
  identifier,
  api,
  limit = 10,
  refetchOnMount = false,
  enabled = true,
  infiniteScroll = true,
  select,
  id = "",
  cacheTime = 5000,
  staleTime = 0
) => {
  const Session = true;

  const useQueryOptions = {
    select: (data) => {
      return select ? data[0] : data;
    },
    refetchOnMount: refetchOnMount,
    refetchOnWindowFocus: false,
    enabled: enabled,
    cacheTime: cacheTime,
    staleTime: staleTime,
    retry: 1,
  };
  return useInfiniteQuery({
    queryKey: [identifier],
    queryFn: ({ pageParam }) =>
      fetcherFunction({
        api,
        Session,
        lang,
        pageParam,
        limit,
        infiniteScroll,
      }),
    getNextPageParam: (lastPage, pages) => {
      if (pages?.length < lastPage?.meta?.last_page) {
        return pages?.length + 1;
      } else return undefined;
    },
    ...useQueryOptions,
    initialPageParam: 1,
  });
};
export const usePostData = (
  showToasts = false,
  onSuccess,
  authorizedAPI,
  onError
) => {
  const Session = true;

  const posterFunction = async ({
    api,
    data,
    addToken = true,
    file,
    method = "POST",
  }) => {
    const ContentType = file ? "multipart/form-data" : "application/json";

    const headers = {
      "Content-Type": ContentType,
      lang,
    };

    const options = {
      url: api,
      method: method,
      headers: headers,
      //data: JSON.stringify(data),
      data: data,
      withCredentials: Session || addToken ? true : false,
    };

    const res = await axiosInstance(options);
    return res.data;
  };
  const usePostDataOptions = {
    onSuccess: (data) => {
      showToasts && message.success(data.message);
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      onError && onError(error);
      console.log("posterError", error);

      showToasts &&
        message.error(error?.response?.data?.message || "حدث خطأ ما");
      authorizedAPI &&
        error?.response?.status === 401 &&
        navigate(`/login`, { replace: true });
    },
  };
  const navigate = useNavigate();

  return useMutation({
    mutationFn: posterFunction,
    ...usePostDataOptions,
  });
};
